import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Spinner,
} from '@patternfly/react-core';
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import { SyncIcon } from '@patternfly/react-icons';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import {
  SessionEventForwardingHealthResponse,
  parseSessionEventForwardingHealthResponse,
} from 'src/contracts/reporting';
import PushDeliveryMonitor from './PushDeliveryMonitor';
import './forwarding.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_INTERVAL_MS = 3000;

type ThroughputRates = {
  processedPerSecond: number | null;
  forwardedPerSecond: number | null;
  failedPerSecond: number | null;
};

type ThroughputSnapshot = {
  capturedAtMs: number;
  processedTotal: number;
  forwardedTotal: number;
  failedTotal: number;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleString();
};

const formatNumber = (value?: number | null, fractionDigits = 2) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: 0,
  });
};

const bucketRows = <TBuckets extends Record<string, number>>(
  buckets: TBuckets,
  labels: Array<{ key: keyof TBuckets; label: string }>,
) =>
  labels.map((row) => (
    <tr key={String(row.key)}>
      <td>{row.label}</td>
      <td>{buckets[row.key].toLocaleString()}</td>
    </tr>
  ));

const SessionEventForwardingMonitor = () => {
  const authedFetch = useAuthedFetch();
  const [health, setHealth] = useState<SessionEventForwardingHealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rates, setRates] = useState<ThroughputRates>({
    processedPerSecond: null,
    forwardedPerSecond: null,
    failedPerSecond: null,
  });
  const previousSnapshotRef = useRef<ThroughputSnapshot | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading((current) => current || !health);
    try {
      const response = await authedFetch(`${API_BASE}/api/reporting/forwarding/session-events`);
      if (!response.ok) {
        throw new Error(`Forwarding metrics request failed (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parseSessionEventForwardingHealthResponse(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      const nextValue = parsed.value;
      const nowMs = Date.now();
      const previous = previousSnapshotRef.current;
      if (previous) {
        const elapsedSeconds = (nowMs - previous.capturedAtMs) / 1000;
        if (elapsedSeconds > 0) {
          setRates({
            processedPerSecond:
              (nextValue.processed_total - previous.processedTotal) / elapsedSeconds,
            forwardedPerSecond:
              (nextValue.forwarded_total - previous.forwardedTotal) / elapsedSeconds,
            failedPerSecond:
              (nextValue.forward_failed_total - previous.failedTotal) / elapsedSeconds,
          });
        }
      }
      previousSnapshotRef.current = {
        capturedAtMs: nowMs,
        processedTotal: nextValue.processed_total,
        forwardedTotal: nextValue.forwarded_total,
        failedTotal: nextValue.forward_failed_total,
      };
      setHealth(nextValue);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forwarding metrics');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, health]);

  useEffect(() => {
    void fetchHealth();
    const interval = window.setInterval(() => {
      void fetchHealth();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [fetchHealth]);

  const successRate = useMemo(() => {
    if (!health || health.processed_total <= 0) {
      return null;
    }
    return (health.forwarded_total / health.processed_total) * 100;
  }, [health]);

  return (
    <div className="forwarding-monitor-page">
      <PageHeader
        title="Delivery Monitor"
        subtitle="Session-event forwarding and queue push delivery health"
      />

      <Card>
        <CardBody>
          <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsCenter' }}>
            <FlexItem>
              <CardTitle>Session Event Forwarding</CardTitle>
            </FlexItem>
            <FlexItem>
              <Button onClick={() => void fetchHealth()} icon={<SyncIcon />} isDisabled={loading}>
                Refresh
              </Button>
            </FlexItem>
            <FlexItem>
              <Label color="blue">Polling every {POLL_INTERVAL_MS / 1000}s</Label>
            </FlexItem>
            <FlexItem>
              <Label color="green">
                Last update: {formatDateTime(health?.generated_at)}
              </Label>
            </FlexItem>
          </Flex>
        </CardBody>
      </Card>

      {error && (
        <Alert isInline variant="danger" title="Failed to load forwarding metrics">
          {error}
        </Alert>
      )}

      {loading && !health && (
        <Bullseye style={{ minHeight: 120 }}>
          <Spinner />
        </Bullseye>
      )}

      {health && (
        <Grid hasGutter className="forwarding-monitor-grid">
          <GridItem span={4}>
            <Card>
              <CardTitle>Message Totals</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Processed</span>
                  <strong>{health.processed_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Forwarded</span>
                  <strong>{health.forwarded_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Failed</span>
                  <strong>{health.forward_failed_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Retries</span>
                  <strong>{health.retries_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Success Rate</span>
                  <strong>{typeof successRate === 'number' ? `${formatNumber(successRate)}%` : '—'}</strong>
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={4}>
            <Card>
              <CardTitle>Throughput (per second)</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Processed/sec</span>
                  <strong>{formatNumber(rates.processedPerSecond)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Forwarded/sec</span>
                  <strong>{formatNumber(rates.forwardedPerSecond)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Failed/sec</span>
                  <strong>{formatNumber(rates.failedPerSecond)}</strong>
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={4}>
            <Card>
              <CardTitle>Latest Forward Status</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Event Type</span>
                  <strong>{health.last_event_type || '—'}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Stream ID</span>
                  <strong>{health.last_stream_id || '—'}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Status Code</span>
                  <strong>{health.last_status_code ?? '—'}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last Forwarded</span>
                  <strong>{formatDateTime(health.last_forwarded_at)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last Failure</span>
                  <strong>{formatDateTime(health.last_failure_at)}</strong>
                </div>
                <div className="forwarding-error-text">
                  {health.last_error_type ? `${health.last_error_type}: ` : ''}
                  {health.last_error_message || 'No recent failure message'}
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card>
              <CardTitle>Forward Latency (ms)</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Last</span>
                  <strong>{formatNumber(health.last_forward_duration_ms)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Avg</span>
                  <strong>{formatNumber(health.avg_forward_duration_ms)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Max</span>
                  <strong>{formatNumber(health.max_forward_duration_ms)}</strong>
                </div>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucketRows(health.forward_latency_buckets, [
                      { key: 'le_100ms', label: '<= 100ms' },
                      { key: 'le_250ms', label: '<= 250ms' },
                      { key: 'le_500ms', label: '<= 500ms' },
                      { key: 'le_1000ms', label: '<= 1000ms' },
                      { key: 'gt_1000ms', label: '> 1000ms' },
                    ])}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card>
              <CardTitle>Pickup Wait (ms)</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Last</span>
                  <strong>{formatNumber(health.last_pickup_wait_ms)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Avg</span>
                  <strong>{formatNumber(health.avg_pickup_wait_ms)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Max</span>
                  <strong>{formatNumber(health.max_pickup_wait_ms)}</strong>
                </div>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucketRows(health.stream_lag_buckets, [
                      { key: 'le_1000ms', label: '<= 1000ms' },
                      { key: 'le_5000ms', label: '<= 5000ms' },
                      { key: 'le_10000ms', label: '<= 10000ms' },
                      { key: 'le_30000ms', label: '<= 30000ms' },
                      { key: 'gt_30000ms', label: '> 30000ms' },
                    ])}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={12}>
            <Card>
              <CardTitle>Core API Stage Timings (ms)</CardTitle>
              <CardBody>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th>Last</th>
                      <th>Avg</th>
                      <th>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>core_api_handler_ms</td>
                      <td>{formatNumber(health.last_core_api_handler_ms)}</td>
                      <td>{formatNumber(health.avg_core_api_handler_ms)}</td>
                      <td>{formatNumber(health.max_core_api_handler_ms)}</td>
                    </tr>
                    <tr>
                      <td>ws_emit_ms</td>
                      <td>{formatNumber(health.last_ws_emit_ms)}</td>
                      <td>{formatNumber(health.avg_ws_emit_ms)}</td>
                      <td>{formatNumber(health.max_ws_emit_ms)}</td>
                    </tr>
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={12}>
            <Card>
              <CardTitle>Event Type Breakdown</CardTitle>
              <CardBody>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Event Type</th>
                      <th>Forwarded</th>
                      <th>Failed</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.event_type_breakdown.map((item) => (
                      <tr key={item.event_type}>
                        <td>{item.event_type}</td>
                        <td>{item.forwarded_count.toLocaleString()}</td>
                        <td>{item.failed_count.toLocaleString()}</td>
                        <td>{(item.forwarded_count + item.failed_count).toLocaleString()}</td>
                      </tr>
                    ))}
                    {health.event_type_breakdown.length === 0 && (
                      <tr>
                        <td colSpan={4}>No event data yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      )}

      <PushDeliveryMonitor />
    </div>
  );
};

export default SessionEventForwardingMonitor;
